/**
 * src/lib/dataFreshness.ts — how old is the committed SOFR data, and does that mean the
 * refresh pipeline has died? (§10.9)
 *
 * Written after a real outage: GitLab's shared-runner minutes ran out on 2026-08-03, every
 * scheduled `sofr_update` failed from then on, and the committed data sat frozen at the
 * 2026-07-31 observation for three weeks without anyone noticing. The pipeline WAS failing
 * loudly — it was just failing somewhere nobody looks. A scheduled job that fails is
 * indistinguishable from one that had nothing to do.
 *
 * So this check reports where the data is actually read: on the dashboard. CI is
 * deliberately NOT the home for it. Two further reasons beyond visibility: a hard test on
 * the committed file would block every unrelated MR, and — worse — it would block the very
 * MR that fixes the pipeline, which is a deadlock.
 *
 * Pure and clock-injected: `now` is always passed in, never read here, so the thresholds
 * can be tested at exact boundaries instead of against a moving wall clock.
 */

/** Age past which the data is older than a weekly refresh should ever leave it. */
export const SOFR_STALE_AFTER_DAYS = 10;
/** Age that can only mean the scheduled refresh has stopped running. */
export const SOFR_DEAD_AFTER_DAYS = 21;

export type FreshnessLevel = 'ok' | 'stale' | 'dead';

export interface SofrFreshness {
  /** Whole calendar days between the latest observation and `now`; 0 if not yet known. */
  ageDays: number;
  level: FreshnessLevel;
  /** User-facing explanation, or null when the data is fresh. */
  message: string | null;
}

const MS_PER_DAY = 86_400_000;

/**
 * Grade the age of the latest SOFR observation.
 *
 * Calendar days, not business days: the point is to catch a dead pipeline, and a threshold
 * that needs a holiday calendar to evaluate is a threshold that will itself rot. The
 * `stale` bound allows for a weekly schedule plus FRED's publication lag plus a weekend;
 * `dead` is roughly two missed weekly runs, far enough out that it cannot cry wolf.
 *
 * An empty or unparseable date yields `ok`: the loader has its own warning path for data
 * it could not read, and claiming "the pipeline is dead" on a parse failure would be
 * reporting the wrong fault.
 */
export function sofrFreshness(latestDate: string, now: Date): SofrFreshness {
  if (!latestDate) return { ageDays: 0, level: 'ok', message: null };
  const observed = Date.parse(`${latestDate}T00:00:00Z`);
  if (Number.isNaN(observed)) return { ageDays: 0, level: 'ok', message: null };

  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const ageDays = Math.max(0, Math.floor((today - observed) / MS_PER_DAY));

  if (ageDays > SOFR_DEAD_AFTER_DAYS) {
    return {
      ageDays,
      level: 'dead',
      message:
        `SOFR data is ${ageDays} days old — the scheduled refresh has stopped running. ` +
        `Rates shown are last known, not current; enter your own base rate until it is fixed.`,
    };
  }
  if (ageDays > SOFR_STALE_AFTER_DAYS) {
    return {
      ageDays,
      level: 'stale',
      message: `SOFR data is ${ageDays} days old — a scheduled refresh may have been missed.`,
    };
  }
  return { ageDays, level: 'ok', message: null };
}
